import React, { useRef, useEffect, useState } from 'react';

export interface Tab {
  id: string;
  label: string;
  icon?: React.ReactNode;
  badge?: string | number;
}

interface SlidingTabsProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
}

const SlidingTabs: React.FC<SlidingTabsProps> = ({ tabs, activeTab, onTabChange }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [sliderStyle, setSliderStyle] = useState<React.CSSProperties>({});

  // Update slider position when active tab changes
  useEffect(() => {
    const activeButton = tabRefs.current.get(activeTab);
    const container = containerRef.current;

    if (activeButton && container) {
      const containerRect = container.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();

      setSliderStyle({
        width: buttonRect.width,
        transform: `translateX(${buttonRect.left - containerRect.left}px)`,
      });
    }
  }, [activeTab]);

  // Recalculate on resize
  useEffect(() => {
    const handleResize = () => {
      const activeButton = tabRefs.current.get(activeTab);
      const container = containerRef.current;

      if (activeButton && container) {
        const containerRect = container.getBoundingClientRect();
        const buttonRect = activeButton.getBoundingClientRect();

        setSliderStyle({
          width: buttonRect.width,
          transform: `translateX(${buttonRect.left - containerRect.left}px)`,
        });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [activeTab]);

  return (
    <div
      ref={containerRef}
      className="relative mb-4 sm:mb-6 flex items-center bg-gray-100 dark:bg-slate-900/50 rounded-xl p-1 border border-gray-200 dark:border-slate-700/50"
    >
      {/* Sliding background indicator */}
      <div
        className="absolute top-1 bottom-1 rounded-lg bg-gradient-to-r from-gray-800 to-gray-900 dark:from-blue-600 dark:to-purple-600 shadow-lg shadow-gray-500/25 dark:shadow-blue-500/25 transition-all duration-300 ease-out"
        style={sliderStyle}
      />

      {/* Tab buttons */}
      {tabs.map((tab) => (
        <button
          key={tab.id}
          ref={(el) => {
            if (el) tabRefs.current.set(tab.id, el);
          }}
          onClick={() => onTabChange(tab.id)}
          className={`relative z-10 flex-1 px-2 sm:px-6 py-2.5 sm:py-3 rounded-lg font-medium sm:font-semibold transition-colors duration-200 flex items-center justify-center space-x-1.5 sm:space-x-2 text-sm sm:text-base ${
            activeTab === tab.id
              ? 'text-white'
              : 'text-gray-500 hover:text-gray-900 dark:text-slate-400 dark:hover:text-white'
          }`}
        >
          <span className="[&>svg]:w-4 [&>svg]:h-4 sm:[&>svg]:w-5 sm:[&>svg]:h-5">{tab.icon}</span>
          <span className="hidden xs:inline sm:inline">{tab.label}</span>
          {tab.badge !== undefined && (
            <span
              className={`px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${
                activeTab === tab.id ? 'bg-white/20' : 'bg-gray-200 dark:bg-slate-700/50'
              }`}
            >
              {tab.badge}
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

export default SlidingTabs;
